import { cacheKeys } from "@constants/cache-keys";
import { events } from "@constants/tracking";
import { PurchaseDialog } from "@features/dashboard/components/PurchaseDialog";
import { getWeakTagsIds } from "@features/dashboard/helpers/common";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import type { Document } from "@features/podcasts/types";
import { SessionConfigDialog } from "@features/session-config/components/SessionConfigDialog";
import { hasProductAccess } from "@helpers/content/tag";
import { getShowStudyPackViews } from "@helpers/exams";
import i18n from "@helpers/lib/i18n";
import { isWeb } from "@helpers/platform";
import { useAppStore } from "@hooks/useAppStore";
import { useAppTheme } from "@hooks/useAppTheme";
import useCacheKey from "@hooks/useCacheKey";
import useClerkUser from "@hooks/useClerkUser";
import { useCustomNavigation } from "@hooks/useCustomNavigation";
import { useDevice } from "@hooks/useDevice";
import { isDev, isStaging } from "@memorang/constants/src/urls";
import type { BundleMetaData } from "@memorang/types/src/bundle";
import type { MixedItemDist } from "@memorang/types/src/tag";
import { ScrollButtonFlatlist } from "@memorang/ui/src/components/ChatUI/ScrollButtonFlatlist";
import LoaderDialog from "@memorang/ui/src/components/LoaderDialog";
import { useQueryClient } from "@tanstack/react-query";
import { trackCustomEvent } from "analytics";
import type { QuickActionsChipType } from "components/QuickActionChips/types";
import SetExamDateContainer from "components/SetExamDateContainer";
import { router } from "expo-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Chip } from "react-native-paper";
import useDiagnostic from "./useDiagnostic";

const QuickActionItem = ({
	chip,
	handleQuickActionPress,
}: {
	chip: QuickActionsChipType;
	handleQuickActionPress: (action: QuickActionsChipType["type"]) => void;
}) => {
	const theme = useAppTheme();
	return (
		<Chip
			icon={chip.icon}
			mode={chip.selected ? "flat" : "outlined"}
			compact
			textStyle={{ fontSize: theme.fonts.labelLarge.fontSize }}
			selected={chip.selected}
			style={{
				// maxHeight: 32,
				alignItems: "center",
			}}
			onPress={() => {
				handleQuickActionPress(chip.type);
			}}
		>
			{chip.label}
		</Chip>
	);
};

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
const QuickActionsContainer = () => {
	const queryClient = useQueryClient();

	const { t } = useTranslation();
	const {
		currentExamContext: {
			studyPackProductNumericId,
			studyPackCollectionId,
			practiceTestProductNumericId,
			studyPackProductId,
			examId,
		},
	} = useExamContext();
	const { userPublicMetadataId: userId } = useClerkUser();
	const [showDatePicker, setShowDatePicker] = useState(false);
	const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);
	const [showSessionConfigDialog, setShowSessionConfigDialog] = useState(false);
	const [selectedWeakTags, setSelectedWeakTags] = useState<string[]>([]);
	const { handleSessionConfigNavigation } = useCustomNavigation();

	const showDiagnostic =
		useAppStore((state) => state.app?.showDiagnostic) && isWeb;

	const { isMobile } = useDevice();

	const { baseCacheKey } = useCacheKey();

	const showStudyPackViews = getShowStudyPackViews(studyPackCollectionId);

	const { isCreatingSession, messages, handleViewReport, handleCreateSession } =
		useDiagnostic();

	const toggleShowSessionConfigDialog = () => {
		setShowSessionConfigDialog((prev) => !prev);
	};

	const toggleShowDatePicker = () => {
		setShowDatePicker((prev) => !prev);
	};

	const toggleShowPurchaseDialog = () => {
		setShowPurchaseDialog((prev) => !prev);
	};

	const cachedPodcasts = queryClient.getQueryData<Document[]>([
		baseCacheKey,
		cacheKeys.podcasts,
	]);

	const cachedProductMetadata = queryClient.getQueryData<BundleMetaData>([
		cacheKeys.productMetadata,
		studyPackProductId,
	]);
	const cachedMixedItemDist = queryClient.getQueryData<MixedItemDist>([
		cacheKeys.home,
		userId,
		"COMMON_DISTRIBUTION",
		examId,
		...(studyPackCollectionId ? [studyPackCollectionId] : []),
	]);
	const exams = cachedMixedItemDist?.exams;

	const hasCompletedSomeDiagnosticExams = exams?.some(
		(exam) => exam.status === "COMPLETE" && exam.subType === "DIAGNOSTIC",
	);

	const hasDiagnostic = exams?.some(
		(exam) => exam.status === "NOT_STARTED" && exam.subType === "DIAGNOSTIC",
	);
	const hasAccess =
		isDev && !isStaging
			? true
			: hasProductAccess(cachedProductMetadata, studyPackProductNumericId);

	const diagnosticLabel = useAppStore((state) => state.exam.diagnosticLabel);

	const weakTagsIds = cachedMixedItemDist
		? getWeakTagsIds(cachedMixedItemDist)
		: [];

	const hasProgress = weakTagsIds.length > 0;
	const hasPodcasts = cachedPodcasts && cachedPodcasts.length > 0;
	// const hasExamDateSet = !!examDate;

	const actionChips: QuickActionsChipType[] = [
		...(hasPodcasts
			? [
					{
						label: t("dashboard.actions.viewPodcasts"),
						type: "viewPodcasts" as const,
						icon: "google-podcast" as const,
						tooltip: t("dashboard.actions.viewPodcastsTooltip"),
					},
				]
			: []),
		...(hasCompletedSomeDiagnosticExams
			? [
					{
						label: t("dashboard.actions.viewDiagnostic"),
						type: "viewDiagnostic" as const,
						icon: "chart-bar" as const,
						tooltip: t("dashboard.actions.viewDiagnosticTooltip"),
					},
				]
			: []),
		...(hasDiagnostic && showDiagnostic && practiceTestProductNumericId
			? [
					{
						label: diagnosticLabel
							? `Take ${diagnosticLabel[i18n.language]}`
							: t("dashboard.actions.takeDiagnostic"),
						type: "takeDiagnostic" as const,
						icon: "play-outline" as const,
						tooltip: t("dashboard.actions.takeDiagnosticTooltip"),
					},
				]
			: []),
		...(showStudyPackViews
			? [
					{
						label: t("dashboard.actions.startCustomSession"),
						type: "customSession" as const,
						icon: "play-outline" as const,
						tooltip: t("dashboard.actions.startCustomSessionTooltip"),
					},
				]
			: []),
		// ...(hasExamDateSet
		// 	? []
		// 	: [
		// 			{
		// 				label: t("dashboard.actions.setExamDate"),
		// 				type: "setExamDate" as const,
		// 				icon: "calendar-outline" as const,
		// 				tooltip: t("dashboard.actions.setExamDateTooltip"),
		// 			},
		// 		]),
		...(hasProgress && showStudyPackViews
			? [
					{
						label: t("dashboard.actions.studyWeak"),
						type: "weak" as const,
						icon: "alert-circle-outline",
						tooltip: t("dashboard.actions.studyWeak"),
					},
				]
			: []),
		...(hasProgress
			? [
					{
						label: t("dashboard.actions.viewProgress"),
						type: "progress" as const,
						icon: "chart-timeline-variant-shimmer" as const,
						tooltip: t("dashboard.actions.viewProgress"),
					},
				]
			: []),
		{
			label: t("dashboard.actions.seeRank"),
			type: "leaderboard",
			icon: "trophy-outline",
			tooltip: t("dashboard.actions.seeRankings"),
		},

		...(hasAccess || !showStudyPackViews
			? []
			: [
					{
						label: t("dashboard.actions.upgradeToPremium"),
						type: "purchase" as const,
						icon: "cart-outline" as const,
						tooltip: t("dashboard.actions.upgradeToPremiumTooltip"),
					},
				]),
		...(hasAccess && showStudyPackViews
			? [
					{
						label: t("dashboard.actions.extendAccess"),
						type: "purchase" as const,
						icon: "cart-outline" as const,
						tooltip: t("dashboard.actions.extendAccessTooltip"),
					},
				]
			: []),
	];

	const handleShowPurchase = () => {
		if (isWeb) {
			toggleShowPurchaseDialog?.();
		} else {
			router.push({
				pathname: "/purchase",
				params: {
					productType: "STUDY_PACK",
				},
			});
		}
	};

	const handleQuickActionPress = (action: QuickActionsChipType["type"]) => {
		trackCustomEvent({
			eventName: events.quickActionClicked,
			action,
		});
		switch (action) {
			case "weak":
				{
					setSelectedWeakTags(weakTagsIds.map((id) => id.toString()));
					handleSessionConfigNavigation(toggleShowSessionConfigDialog, {
						selectedTagIds: weakTagsIds.map((id) => id.toString()),
						reset: "true",
						studyRecommenedTopics: "true",
					});
				}
				break;
			case "leaderboard":
				router.push("/(protected)/(tabs)/leaderboard");
				break;
			case "viewPodcasts":
				router.push("/(protected)/(tabs)/podcasts");
				break;
			case "progress":
				router.push("/(protected)/(tabs)/progress");
				break;
			case "purchase":
				handleShowPurchase();
				break;
			case "setExamDate":
				toggleShowDatePicker();
				break;
			case "customSession":
				handleSessionConfigNavigation(toggleShowSessionConfigDialog);
				break;
			case "viewDiagnostic":
				{
					const completedExam = exams?.find(
						(exam) => exam.status === "COMPLETE",
					);
					if (completedExam?.numericId) {
						handleViewReport(completedExam.numericId);
					}
				}
				break;
			case "takeDiagnostic":
				{
					const notCompletedExam = exams?.find(
						(exam) =>
							exam.status !== "COMPLETE" && exam.subType === "DIAGNOSTIC",
					);

					if (notCompletedExam?.id && exams) {
						handleCreateSession({
							exam: notCompletedExam,
						});
					}
				}
				break;
			default:
				break;
		}
	};
	return (
		<>
			{showDatePicker ? (
				<SetExamDateContainer
					showDatePicker={showDatePicker}
					toggleShowDatePicker={toggleShowDatePicker}
				/>
			) : null}
			{showPurchaseDialog && (
				<PurchaseDialog
					visible={showPurchaseDialog}
					handleClose={toggleShowPurchaseDialog}
					productType="STUDY_PACK"
				/>
			)}
			{showSessionConfigDialog ? (
				<SessionConfigDialog
					visible={showSessionConfigDialog}
					selectedTagIds={selectedWeakTags?.map((id) => Number(id))}
					handleClose={() => {
						setSelectedWeakTags([]);
						toggleShowSessionConfigDialog();
					}}
				/>
			) : null}
			{isCreatingSession ? (
				<LoaderDialog
					visible
					messages={messages.map((message) => ({
						text: message,
					}))}
				/>
			) : null}
			<ScrollButtonFlatlist
				data={actionChips}
				contentContainerStyle={{
					gap: 8,
					paddingHorizontal: isMobile ? 16 : 0,
				}}
				renderItem={({ item }) => (
					<QuickActionItem
						chip={item}
						handleQuickActionPress={handleQuickActionPress}
					/>
				)}
			/>
		</>
	);
};

export default QuickActionsContainer;
